import { CampaignNodeLike, collectInvalidVariableErrors } from "./campaign-variable-errors";
import { nodesContainSignatureVariable } from "./campaign-placeholders";

import { analyzeDisconnectedNodes, isGraphConnected } from "./campaign-graph";

/**
 * Why a campaign cannot run. The code is what UI outside the builder keys its short label off; the
 * `message` is the exact English sentence the builder has always shown, kept verbatim so extracting
 * this rulebook out of `CampaignPage` changed nothing a user reads there.
 */
export enum CampaignValidationCode {
	EMAIL_SUBJECT_AND_MESSAGE_MISSING = "EMAIL_SUBJECT_AND_MESSAGE_MISSING",
	EMAIL_SUBJECT_MISSING = "EMAIL_SUBJECT_MISSING",
	EMAIL_MESSAGE_MISSING = "EMAIL_MESSAGE_MISSING",
	LINKEDIN_MESSAGE_MISSING = "LINKEDIN_MESSAGE_MISSING",
	INMAIL_MESSAGE_MISSING = "INMAIL_MESSAGE_MISSING",
	INMAIL_SUBJECT_MISSING = "INMAIL_SUBJECT_MISSING",
	TIMEZONE_MISSING = "TIMEZONE_MISSING",
	SCHEDULE_MISSING = "SCHEDULE_MISSING",
	NO_ACTIVE_SENDER = "NO_ACTIVE_SENDER",
	TOO_FEW_NODES = "TOO_FEW_NODES",
	STARTER_DISCONNECTED = "STARTER_DISCONNECTED",
	NODES_DISCONNECTED = "NODES_DISCONNECTED",
	INVALID_VARIABLE = "INVALID_VARIABLE",
	NO_ACTIVE_EMAIL_SENDER = "NO_ACTIVE_EMAIL_SENDER",
	NO_ACTIVE_LINKEDIN_SENDER = "NO_ACTIVE_LINKEDIN_SENDER",
	SENDER_SIGNATURE_MISSING = "SENDER_SIGNATURE_MISSING",
}

export interface CampaignValidationError {
	code: CampaignValidationCode;
	message: string;
}

export interface CampaignValidationResult {
	isValid: boolean;
	errors: CampaignValidationError[];
}

/** A flow node, structurally typed so the saved config and the live canvas both satisfy it. */
export interface CampaignValidationNode extends CampaignNodeLike {
	id: string;
	type?: string;
}

export interface CampaignValidationEdge {
	source: string;
	target: string;
}

export interface CampaignValidationSender {
	active?: boolean;
	type?: string;
	emailId?: number;
	emailAddress?: string;
}

/** The campaign fields the rules read; a superset lands here from both the builder and the list. */
export interface CampaignValidationCampaign {
	timezone?: string | null;
	schedule?: object | null;
	senders?: CampaignValidationSender[];
	initialNodes?: CampaignValidationNode[];
}

/** One sending address, for the `{signature}` rule (a signature is nullable in the API payload). */
export interface CampaignValidationEmail {
	id?: number;
	signature?: string | null;
}

export interface ValidateCampaignInput {
	campaign: CampaignValidationCampaign;
	/** The live canvas inside the builder; the saved `initialNodes` everywhere else. */
	nodes: CampaignValidationNode[];
	edges: CampaignValidationEdge[];
	/** Placeholder keys this campaign may reference (from `getSuggestions`). */
	validKeys: string[];
	/** Every sending address the company has, matched to senders by `emailId`. */
	senderEmails: CampaignValidationEmail[];
}

const error = (code: CampaignValidationCode, message: string): CampaignValidationError => ({
	code,
	message,
});

/** Walks back along the edges to the node feeding `nodeId`, or null at the start of the branch. */
const previousNodeOf = (
	nodeId: string,
	nodes: CampaignValidationNode[],
	edges: CampaignValidationEdge[]
): CampaignValidationNode | null => {
	const edge = edges.find((candidate) => candidate.target === nodeId);

	return edge ? (nodes.find((node) => node.id === edge.source) ?? null) : null;
};

/**
 * Whether an email node inherits a subject from an earlier email in the same branch — a follow-up
 * keeps the thread's subject, so only the first email of a branch has to carry one itself.
 */
const hasPrecedingEmailNode = (
	node: CampaignValidationNode,
	nodes: CampaignValidationNode[],
	edges: CampaignValidationEdge[]
): boolean => {
	// `seen` is not in the original memo: it only ever walked the one open campaign, whereas this
	// now runs over every campaign of a list on render, so a cycle would hang the page instead of
	// one canvas. A cycle has no first email either way, so bailing out answers the question.
	const seen = new Set<string>([node.id]);
	let previous = previousNodeOf(node.id, nodes, edges);

	while (previous && previous.type !== "starterNode" && !seen.has(previous.id)) {
		if (previous.type === "emailNode") {
			return true;
		}

		seen.add(previous.id);
		previous = previousNodeOf(previous.id, nodes, edges);
	}

	return false;
};

/** Per-node content rules: every step that sends must have something to send. */
const collectNodeContentErrors = (
	nodes: CampaignValidationNode[],
	edges: CampaignValidationEdge[]
): CampaignValidationError[] => {
	const errors: CampaignValidationError[] = [];

	for (const node of nodes) {
		if (node.type === "emailNode") {
			const inheritsSubject = hasPrecedingEmailNode(node, nodes, edges);
			const { subject, message } = node.data ?? {};

			if (!subject && !inheritsSubject && !message) {
				errors.push(
					error(
						CampaignValidationCode.EMAIL_SUBJECT_AND_MESSAGE_MISSING,
						"Email node must have a subject and message"
					)
				);
			}

			if (!subject && !inheritsSubject && message) {
				errors.push(
					error(CampaignValidationCode.EMAIL_SUBJECT_MISSING, "Email node must have a subject")
				);
			}

			if ((subject || inheritsSubject) && !message) {
				errors.push(
					error(CampaignValidationCode.EMAIL_MESSAGE_MISSING, "Email node must have a message")
				);
			}
		} else if (node.type === "linkedinNode" && !node.data?.message) {
			errors.push(
				error(CampaignValidationCode.LINKEDIN_MESSAGE_MISSING, "Linkedin node must have a message")
			);
		} else if (node.type === "linkedinInmailNode") {
			if (!node.data?.message) {
				errors.push(
					error(
						CampaignValidationCode.INMAIL_MESSAGE_MISSING,
						"Linkedin inmail node must have a message"
					)
				);
			} else if (!node.data?.subject) {
				errors.push(
					error(
						CampaignValidationCode.INMAIL_SUBJECT_MISSING,
						"Linkedin inmail node must have a subject"
					)
				);
			}
		}
	}

	return errors;
};

/** Settings rules: when it may send, and who sends it. */
const collectSettingsErrors = (campaign: CampaignValidationCampaign): CampaignValidationError[] => {
	const errors: CampaignValidationError[] = [];

	if (!campaign.timezone) {
		errors.push(error(CampaignValidationCode.TIMEZONE_MISSING, "Campaign must have a timezone"));
	}

	if (!campaign.schedule) {
		errors.push(error(CampaignValidationCode.SCHEDULE_MISSING, "Campaign must have a schedule"));
	}

	if ((campaign.senders?.filter((sender) => sender.active).length ?? 0) <= 0) {
		errors.push(error(CampaignValidationCode.NO_ACTIVE_SENDER, "Campaign must have senders"));
	}

	return errors;
};

/** Shape rules: the canvas has to be a single connected sequence with at least one real step. */
const collectGraphErrors = (
	nodes: CampaignValidationNode[],
	edges: CampaignValidationEdge[]
): CampaignValidationError[] => {
	const errors: CampaignValidationError[] = [];

	if (nodes.length <= 1) {
		errors.push(error(CampaignValidationCode.TOO_FEW_NODES, "Campaign must have at least 1 node"));
	}

	if (!isGraphConnected(nodes, edges)) {
		const analysis = analyzeDisconnectedNodes(nodes, edges);

		errors.push(
			analysis.starterDisconnected
				? error(
						CampaignValidationCode.STARTER_DISCONNECTED,
						"Start node is disconnected — drag to reconnect"
					)
				: error(
						CampaignValidationCode.NODES_DISCONNECTED,
						"Some nodes are disconnected — drag to reconnect or remove them"
					)
		);
	}

	return errors;
};

/**
 * Email-channel rules: an email step needs an active email sender, and `{signature}` needs every
 * one of those senders to actually have a signature saved.
 */
const collectEmailSenderErrors = (
	campaign: CampaignValidationCampaign,
	nodeSources: CampaignValidationNode[][],
	senderEmails: CampaignValidationEmail[]
): CampaignValidationError[] => {
	const errors: CampaignValidationError[] = [];
	const hasAnyEmailNode = nodeSources[0]?.some((node) => node.type === "emailNode") ?? false;
	const activeEmailSenders =
		campaign.senders?.filter((sender) => sender.active && sender.type === "email") ?? [];

	if (hasAnyEmailNode && activeEmailSenders.length <= 0) {
		errors.push(
			error(
				CampaignValidationCode.NO_ACTIVE_EMAIL_SENDER,
				"Campaign must have at least 1 active sender with an email address"
			)
		);
	}

	if (!nodeSources.some((nodes) => nodesContainSignatureVariable(nodes))) {
		return errors;
	}

	const withoutSignature = activeEmailSenders.filter(
		(sender) => !senderEmails.find((email) => email.id === sender.emailId)?.signature
	);

	if (withoutSignature.length > 0) {
		const missing = withoutSignature.map((sender) => sender.emailAddress).join(", ");

		errors.push(
			error(
				CampaignValidationCode.SENDER_SIGNATURE_MISSING,
				`All sender emails must have a signature when using the {signature} variable (missing: ${missing})`
			)
		);
	}

	return errors;
};

/** The step types that send from a LinkedIn account, as `ScheduleService` sends them. */
const LINKEDIN_STEP_TYPES: readonly string[] = [
	"linkedinNode",
	"linkedinInviteNode",
	"linkedinInmailNode",
	"linkedinVisitNode",
];

/**
 * The LinkedIn twin of {@link collectEmailSenderErrors}' first rule: a LinkedIn step needs an
 * active LinkedIn sender (PREC-4314). Without it the campaign read as valid while its first step
 * could never be sent — list 7200's "English (FDI Agency)" opened with a connection request and
 * had only mailboxes switched on, and its leads sat assigned with nothing scheduled. The branch a
 * lead may never take counts too, as it does for email: "Lead has email → no" still sends.
 */
const collectLinkedinSenderErrors = (
	campaign: CampaignValidationCampaign,
	nodeSources: CampaignValidationNode[][]
): CampaignValidationError[] => {
	const hasAnyLinkedinNode =
		nodeSources[0]?.some((node) => LINKEDIN_STEP_TYPES.includes(node.type ?? "")) ?? false;
	const hasActiveLinkedinSender =
		campaign.senders?.some((sender) => sender.active && sender.type === "linkedin") ?? false;

	if (!hasAnyLinkedinNode || hasActiveLinkedinSender) {
		return [];
	}

	return [
		error(
			CampaignValidationCode.NO_ACTIVE_LINKEDIN_SENDER,
			"Campaign must have at least 1 active LinkedIn sender for its LinkedIn steps"
		),
	];
};

/**
 * The codes that mean a step would send **nothing** — its message body is empty.
 *
 * `EMAIL_SUBJECT_AND_MESSAGE_MISSING` belongs here because it is raised only when the body is
 * empty too; a missing *subject* alone does not, and neither does anything about senders, the
 * schedule or the shape of the graph. See {@link validateCampaignStepBodies} for why the line is
 * drawn exactly here.
 */
export const EMPTY_STEP_BODY_CODES: readonly CampaignValidationCode[] = [
	CampaignValidationCode.EMAIL_SUBJECT_AND_MESSAGE_MISSING,
	CampaignValidationCode.EMAIL_MESSAGE_MISSING,
	CampaignValidationCode.LINKEDIN_MESSAGE_MISSING,
	CampaignValidationCode.INMAIL_MESSAGE_MISSING,
];

/** {@link validateCampaignStepBodies}'s input: the campaign's graph, and nothing else. */
export interface ValidateCampaignStepBodiesInput {
	nodes: CampaignValidationNode[];
	edges: CampaignValidationEdge[];
}

/**
 * Does any step of this campaign have nothing to send? (PREC-4153)
 *
 * This is the part of the rulebook the **API** enforces, and it is deliberately the narrowest part
 * of it. `isValid` is written by a browser and describes the campaign as it was at that save; it
 * does not expire, so it goes on saying "valid" about content that has since been replaced. That
 * is PREC-4153: a campaign's steps were overwritten with a half-finished copy of a sibling
 * campaign, its InMail body left empty, the flag stayed `true`, and activation trusted the flag.
 *
 * Why only empty bodies, when the builder checks much more. The rest of the rulebook reads state
 * that legitimately drifts *after* a campaign was last validated — a mailbox is disconnected, a
 * sender is switched off, a schedule is cleared — and measured against production, enforcing all
 * of it would refuse **27.8%** of the campaigns customers currently hold as valid (253 of them for
 * `NO_ACTIVE_SENDER` alone). Refusing to start a campaign that works is a worse failure than the
 * one this guards against, and the builder already reports those cases the moment anyone opens it.
 *
 * An empty body is different in kind: it is not drift, it cannot become sendable on its own, and a
 * step that would transmit an empty message is never what anybody meant. Against the same
 * production data it matches **8 campaigns (0.8%)** — seven of them internal test lists, none of
 * them a running customer campaign.
 *
 * Note what this means for {@link validateCampaignConfig}: the API's rule set is a strict subset of
 * the builder's, by construction rather than by convention, so the server can never refuse a
 * campaign the builder considers valid.
 */
export const validateCampaignStepBodies = ({
	nodes,
	edges,
}: ValidateCampaignStepBodiesInput): CampaignValidationResult => {
	const errors = collectNodeContentErrors(nodes, edges).filter((candidate) =>
		EMPTY_STEP_BODY_CODES.includes(candidate.code)
	);

	return { isValid: errors.length === 0, errors };
};

/**
 * The campaign builder's rulebook, as a pure function (PREC-3952).
 *
 * It used to live inside `CampaignPage`'s `isValidCampaignConfig` memo, which meant the *reasons* a
 * campaign could not run existed only while that campaign was open in the builder — everywhere else
 * only the persisted `isValid` boolean was available, so a campaign vanished from the assignment
 * pickers with nothing to say for itself. Same rules, same order, same wording; now callable from
 * anywhere that holds a campaign config.
 *
 * Both the live canvas and the saved `initialNodes` are scanned for variables and `{signature}`,
 * exactly as the builder did, so an edit that has not autosaved yet still invalidates.
 */
export const validateCampaignConfig = ({
	campaign,
	nodes,
	edges,
	validKeys,
	senderEmails,
}: ValidateCampaignInput): CampaignValidationResult => {
	const nodeSources: CampaignValidationNode[][] = [
		nodes,
		...(campaign.initialNodes ? [campaign.initialNodes] : []),
	];

	const errors = [
		...collectNodeContentErrors(nodes, edges),
		...collectSettingsErrors(campaign),
		...collectGraphErrors(nodes, edges),
		...collectInvalidVariableErrors(nodeSources, validKeys).map((message) =>
			error(CampaignValidationCode.INVALID_VARIABLE, message)
		),
		...collectEmailSenderErrors(campaign, nodeSources, senderEmails),
		...collectLinkedinSenderErrors(campaign, nodeSources),
	];

	return { isValid: errors.length === 0, errors };
};
